/**
 * MobileSocialHintDetailImage Component for LOOPINO AI Platform
 * Mobile page for viewing detailed social hint information - Image Version
 */

import React, { useState, useCallback, useEffect } from 'react';
import './MobileSocialHintDetailImage.css';
import useLanguage from '../../../hooks/useLanguage';

// Import icons
import iconArrowLeft from '../../../assets/images/icons/icon-arrow-left.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconClose from '../../../assets/images/icons/icon-close.svg';
import iconAI from '../../../assets/images/icons/icon-ai.svg';
import iconArrowRight from '../../../assets/images/icons/icon-arrow-right.svg';

const MobileSocialHintDetailImage = ({
  hint,
  onBack
}) => {
  const { t } = useLanguage();

  // State management
  const [uploadedFiles, setUploadedFiles] = useState(new Map()); // Map of imageId -> [files array]
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [embedsLoading, setEmbedsLoading] = useState({});
  const [carouselStates, setCarouselStates] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdImages, setCreatedImages] = useState([]);
  const [creationProgress, setCreationProgress] = useState(0);
  const [isQueued, setIsQueued] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [imageCarouselIndex, setImageCarouselIndex] = useState(0);

  // Initialize embed loading states and carousel states when hint changes
  useEffect(() => {
    if (hint?.embedUrls) {
      const embedLoadingStates = {};
      const carouselInitialStates = {};

      hint.embedUrls.forEach((url, index) => {
        embedLoadingStates[`detail_${hint.id}_${index}`] = true;
      });

      // Initialize carousel to first item
      carouselInitialStates[hint.id] = 0;

      setEmbedsLoading(embedLoadingStates);
      setCarouselStates(carouselInitialStates);
    }
  }, [hint]);

  // Handle file upload (multiple files per image description)
  const handleFileUpload = useCallback((imageId, files) => {
    const normalizedFiles = Array.from(files || []);
    if (!normalizedFiles.length) {
      return;
    }
    console.log(`[MobileSocialHintDetailImage] Files uploaded for image ${imageId}:`, normalizedFiles);
    const fileDataArray = normalizedFiles.map(file => ({
      id: `${imageId}_${Date.now()}_${Math.random()}`,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 'file',
      url: URL.createObjectURL(file),
      size: file.size,
      uploadedAt: new Date().toISOString()
    }));

    setUploadedFiles(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(imageId) || [];
      const combined = [...existing, ...fileDataArray].slice(0, 3);
      newMap.set(imageId, combined);
      return newMap;
    });
  }, []);

  // Handle single file removal
  const handleFileRemove = useCallback((imageId, fileId) => {
    console.log(`[MobileSocialHintDetailImage] Removing file ${fileId} for image ${imageId}`);
    setUploadedFiles(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(imageId) || [];
      const updated = existing.filter(file => {
        if (file.id === fileId) {
          if (file.url.startsWith('blob:')) {
            URL.revokeObjectURL(file.url);
          }
          return false;
        }
        return true;
      });

      if (updated.length === 0) {
        newMap.delete(imageId);
      } else {
        newMap.set(imageId, updated);
      }
      return newMap;
    });
  }, []);

  // Handle create content click
  const handleCreateClick = useCallback(() => {
    console.log('[MobileSocialHintDetailImage] Create content clicked for hint:', hint?.id);
    setShowCreateModal(true);
  }, [hint]);

  // Handle create content confirmation
  const handleCreateConfirm = useCallback(async () => {
    console.log('[MobileSocialHintDetailImage] Creating content with files:', uploadedFiles);
    setShowCreateModal(false);
    setIsCreating(true);
    setCreationProgress(0);

    // Simulate creation progress
    const progressInterval = setInterval(() => {
      setCreationProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          // Simulate image creation completion
          setTimeout(() => {
            const mockTitles = t('socialHint.detailImage.creation.mockTitles', { returnObjects: true }) || [];
            const mockImages = mockTitles.map((title, index) => ({
              id: index + 1,
              url: `https://picsum.photos/400/600?random=${index + 1}`,
              title
            }));
            setCreatedImages(mockImages);
            setIsCreating(false);
            setImageCarouselIndex(0);
          }, 500);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  }, [uploadedFiles, t]);

  // Handle queue button click
  const handleQueueClick = useCallback(() => {
    console.log('[MobileSocialHintDetailImage] Adding to queue');
    setIsQueued(true);
    setShowQueueModal(true);
  }, []);

  // Handle queue modal close
  const handleQueueModalClose = useCallback(() => {
    setShowQueueModal(false);
  }, []);

  // Handle create modal close
  const handleCreateModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  // Convert Instagram URL to embed URL
  const getInstagramEmbedUrl = (url) => {
    if (!url) return null;

    if (url.includes('instagram.com/p/')) {
      return `${url}embed/`;
    }

    if (url.includes('instagram.com/reel/')) {
      return `${url}embed/`;
    }

    return url;
  };

  // Handle embed load
  const handleEmbedLoad = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle embed error
  const handleEmbedError = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle carousel navigation for Instagram embeds
  const handleCarouselNext = useCallback((hintId, maxIndex) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.min((prev[hintId] || 0) + 1, maxIndex)
    }));
  }, []);

  const handleCarouselPrev = useCallback((hintId) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.max((prev[hintId] || 0) - 1, 0)
    }));
  }, []);

  // Handle carousel dot click
  const handleCarouselDotClick = useCallback((hintId, index) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: index
    }));
  }, []);

  // Handle image carousel navigation
  const handleImageCarouselNext = useCallback(() => {
    setImageCarouselIndex(prev =>
      prev >= createdImages.length - 1 ? 0 : prev + 1
    );
  }, [createdImages.length]);

  const handleImageCarouselPrev = useCallback(() => {
    setImageCarouselIndex(prev =>
      prev <= 0 ? createdImages.length - 1 : prev - 1
    );
  }, [createdImages.length]);

  const getEmbedTypeLabel = useCallback((embedType) => {
    if (!embedType) {
      return '';
    }
    return t(`socialHint.common.embeds.types.${embedType}`, {
      defaultValue: t('socialHint.common.embeds.types.default')
    });
  }, [t]);

  const getIframeTitle = useCallback((index, title, embedType) => {
    const networkLabel = getEmbedTypeLabel(embedType) || embedType;
    return t('socialHint.common.embeds.iframeTitle', {
      index: index + 1,
      title,
      network: networkLabel
    });
  }, [getEmbedTypeLabel, t]);

  // Get image descriptions based on hint category
  const getImageDescriptions = useCallback(() => {
    const imageExamples = t('socialHint.detailImage.descriptions.examples', { returnObjects: true }) || {};
    const category = hint?.category;
    if (category && Array.isArray(imageExamples[category]) && imageExamples[category].length) {
      return imageExamples[category];
    }
    return imageExamples.default || [];
  }, [hint?.category, t]);

  const getFileTypeLabel = useCallback((type) => t(`socialHint.common.fileMeta.types.${type}`, {
    defaultValue: type
  }), [t]);

  const getFileSizeLabel = useCallback((size) => t('socialHint.common.fileMeta.size', {
    size: (size / 1024 / 1024).toFixed(1)
  }), [t]);

  // Render file upload component for multiple images
  const renderFileUpload = (imageId, label) => {
    const uploadedFilesForImage = uploadedFiles.get(imageId) || [];
    const remainingSlots = Math.max(0, 3 - uploadedFilesForImage.length);

    return (
      <div className="mobile-social-hint-detail-image-upload-section">
        {/* Show uploaded files */}
        {uploadedFilesForImage.length > 0 && (
          <div className="mobile-social-hint-detail-image-uploaded-files">
            {uploadedFilesForImage.map((file) => (
              <div key={file.id} className="mobile-project-file-item">
                <div className="mobile-project-file-preview">
                  <img
                    src={file.url}
                    alt={file.name}
                    className="mobile-project-file-image"
                  />
                </div>
                <div className="mobile-project-file-info">
                  <div className="mobile-project-file-name">{file.name}</div>
                  <div className="mobile-project-file-meta">
                    {t('socialHint.common.fileMeta.format', {
                      type: getFileTypeLabel(file.type),
                      size: getFileSizeLabel(file.size)
                    })}
                  </div>
                </div>
                <button
                  className="mobile-social-hint-detail-remove-file"
                  onClick={() => handleFileRemove(imageId, file.id)}
                >
                  <img src={iconClose} alt={t('socialHint.common.alt.remove')} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Show upload area if slots available */}
        {remainingSlots > 0 && (
          <div className="mobile-social-hint-detail-image-upload-area">
            <input
              type="file"
              id={`upload_${imageId}`}
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, remainingSlots);
                if (files.length > 0) {
                  handleFileUpload(imageId, files);
                }
              }}
              style={{ display: 'none' }}
            />
            <label
              htmlFor={`upload_${imageId}`}
              className="mobile-social-hint-detail-image-upload-button"
            >
              <img src={iconUpload} alt={t('socialHint.common.alt.upload')} />
              <span>{t('socialHint.common.upload.cta', { label })}</span>
              <small>{t('socialHint.detailImage.upload.remainingSlots', { count: remainingSlots })}</small>
            </label>
          </div>
        )}
      </div>
    );
  };

  // Render embed section
  const renderEmbedSection = () => {
    if (!hint?.embedUrls || hint.embedUrls.length === 0) return null;

    return (
      <div className="mobile-social-hint-detail-image-preview">
        <h3 className="mobile-social-hint-detail-image-carousel-title">
          <span className="section-emoji">📱</span>
          {t('socialHint.common.embeds.title')}
        </h3>
        {hint.embedUrls && hint.embedUrls.length > 0 && hint.embedType === 'instagram' ? (
          <>
            <div className="mobile-social-hint-detail-image-carousel">
              <div
                className="mobile-social-hint-detail-image-carousel-container"
                style={{
                  transform: `translateX(-${(carouselStates[hint.id] || 0) * 100}%)`
                }}
              >
                {hint.embedUrls.map((embedUrl, embedIndex) => {
                  const embedKey = `detail_${hint.id}_${embedIndex}`;
                  return (
                    <div key={embedIndex} className="mobile-social-hint-detail-image-carousel-slide">
                      <div className="mobile-social-hint-detail-image-embed-container">
                        {embedsLoading[embedKey] && (
                          <div className="mobile-social-hint-detail-image-embed-loading">
                            <div className="mobile-social-hint-detail-image-spinner"></div>
                            <span>{t('socialHint.common.embeds.loading')}</span>
                          </div>
                        )}
                        <iframe
                          src={getInstagramEmbedUrl(embedUrl)}
                          className="mobile-social-hint-detail-image-embed-iframe"
                          frameBorder="0"
                          scrolling="no"
                          allowtransparency="true"
                          onLoad={() => handleEmbedLoad(embedKey)}
                          onError={() => handleEmbedError(embedKey)}
                          title={getIframeTitle(embedIndex, hint.title, hint.embedType)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Carousel indicators */}
            {hint.embedUrls && hint.embedUrls.length > 1 && (
              <div className="mobile-social-hint-detail-image-external-indicators">
                {hint.embedUrls.map((_, dotIndex) => (
                  <button
                    key={dotIndex}
                    className={`mobile-social-hint-detail-image-external-dot ${
                      (carouselStates[hint.id] || 0) === dotIndex ? 'active' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCarouselDotClick(hint.id, dotIndex);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="mobile-social-hint-detail-image-embed-placeholder">
            <div className="embed-preview-icon">📷</div>
            <div className="embed-preview-text">
              <span className="embed-preview-type">{getEmbedTypeLabel(hint.embedType)}</span>
              <span className="embed-preview-label">{t('socialHint.common.embeds.placeholder')}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render image descriptions section
  const renderImageDescriptionsSection = () => {
    const images = getImageDescriptions();

    return (
      <div className="mobile-social-hint-detail-image-descriptions">
        <h3 className="mobile-social-hint-detail-section-title">
          <span className="section-emoji">📸</span>
          {t('socialHint.detailImage.sections.descriptionsTitle')}
        </h3>
        <div className="mobile-social-hint-detail-image-descriptions-list">
          {images.map((image) => (
            <div key={image.id} className="mobile-social-hint-detail-image-description-item">
              <div className="mobile-social-hint-detail-image-description-header">
                <h4 className="mobile-social-hint-detail-image-description-title">{image.title}</h4>
              </div>
              <div className="mobile-social-hint-detail-image-description-instruction">
                <p>{image.description}</p>
              </div>
              {renderFileUpload(image.id, t('socialHint.detailImage.upload.labels.image'))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render created images carousel
  const renderCreatedImagesCarousel = () => {
    if (!createdImages || createdImages.length === 0) return null;

    return (
      <div className="mobile-social-hint-detail-image-created-carousel">
        <div className="mobile-social-hint-detail-image-created-container">
          <div
            className="mobile-social-hint-detail-image-created-track"
            style={{
              transform: `translateX(-${imageCarouselIndex * 100}%)`
            }}
          >
            {createdImages.map((image) => (
              <div key={image.id} className="mobile-social-hint-detail-image-created-slide">
                <img
                  src={image.url}
                  alt={image.title}
                  className="mobile-social-hint-detail-image-created-img"
                />
              </div>
            ))}
          </div>

          {/* Navigation arrows */}
          {createdImages.length > 1 && (
            <>
              <button
                className="mobile-social-hint-detail-image-carousel-nav prev"
                onClick={handleImageCarouselPrev}
              >
                <img src={iconArrowLeft} alt={t('socialHint.detailImage.carousel.prev')} />
              </button>
              <button
                className="mobile-social-hint-detail-image-carousel-nav next"
                onClick={handleImageCarouselNext}
              >
                <img src={iconArrowRight} alt={t('socialHint.detailImage.carousel.next')} />
              </button>
            </>
          )}
        </div>

        {/* Carousel indicators */}
        {createdImages.length > 1 && (
          <div className="mobile-social-hint-detail-image-carousel-indicators">
            {createdImages.map((_, index) => (
              <button
                key={index}
                className={`mobile-social-hint-detail-image-carousel-indicator ${
                  imageCarouselIndex === index ? 'active' : ''
                }`}
                onClick={() => setImageCarouselIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Cleanup uploaded files on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(files => {
        files.forEach(file => {
          if (file.url.startsWith('blob:')) {
            URL.revokeObjectURL(file.url);
          }
        });
      });
    };
  }, [uploadedFiles]);

  // Render loading state
  if (isLoading || !hint) {
    return (
      <div className="mobile-social-hint-detail-image">
        <div className="mobile-social-hint-detail-image-loading">
          <div className="mobile-social-hint-detail-image-spinner"></div>
          <p>{t('socialHint.common.status.loadingHint')}</p>
        </div>
      </div>
    );
  }

  // Calculate total uploaded files
  const totalUploadedFiles = Array.from(uploadedFiles.values()).reduce((total, files) => total + files.length, 0);

  return (
    <div className="mobile-social-hint-detail-image">
      <div className="mobile-social-hint-detail-image-content">
        {/* Header */}
        <div className="mobile-social-hint-detail-image-header">
          <button
            className="mobile-social-hint-detail-image-back"
            onClick={onBack}
          >
            <img src={iconArrowLeft} alt={t('socialHint.common.alt.back')} />
          </button>
          <h1 className="mobile-social-hint-detail-image-title">{hint.title}</h1>
        </div>

        {/* Error message */}
        {error && (
          <div className="mobile-social-hint-detail-image-error-banner">
            {error}
          </div>
        )}

        {/* Hint info */}
        <div className="mobile-social-hint-detail-image-info">
          <div className="mobile-social-hint-detail-image-description">
            <p>{hint.detailedDescription}</p>
          </div>
        </div>

        {/* Creation progress section */}
        {isCreating && (
          <div className="mobile-social-hint-detail-image-creation-section">
            <div className="mobile-social-hint-detail-image-creation-container">
              <div className="mobile-social-hint-detail-image-creation-title">
                <span className="section-emoji">🚀</span>
                {t('socialHint.detailImage.status.creating')}
              </div>
              <div className="mobile-social-hint-detail-image-progress-container-inline">
                <div className="mobile-social-hint-detail-image-progress-bar-inline">
                  <div
                    className="mobile-social-hint-detail-image-progress-fill-inline"
                    style={{ width: `${creationProgress}%` }}
                  />
                </div>
                <div className="mobile-social-hint-detail-image-progress-text-inline">
                  {t('socialHint.common.status.progressValue', {
                    progress: Math.round(creationProgress)
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Created images section */}
        {createdImages.length > 0 && !isCreating && (
          <div className="mobile-social-hint-detail-image-created-section">
            <div className="mobile-social-hint-detail-image-created-video-container">
              <div className="mobile-social-hint-detail-image-created-video-header">
                <h3 className="mobile-social-hint-detail-image-created-video-title">
                  <span className="section-emoji">🎨</span>
                  {t('socialHint.detailImage.status.createdTitle')}
                </h3>
              </div>
              <div className="mobile-social-hint-detail-image-video-preview-inline">
                {renderCreatedImagesCarousel()}
              </div>
              <div className="mobile-social-hint-detail-image-video-actions-inline">
                <button className="mobile-social-hint-detail-image-video-action-button-inline download">
                  <img src={iconUpload} alt={t('socialHint.common.alt.download')} />
                  {t('socialHint.common.actions.download')}
                </button>
                <button
                  className={`mobile-social-hint-detail-image-video-action-button-inline queue ${isQueued ? 'queued' : ''}`}
                  onClick={handleQueueClick}
                  disabled={isQueued}
                >
                  {isQueued ? t('socialHint.common.actions.queued') : t('socialHint.common.actions.queue')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main embed section */}
        <div className="mobile-social-hint-detail-image-embed-section">
          {renderEmbedSection()}
        </div>

        {/* Image descriptions section */}
        <div className="mobile-social-hint-detail-image-section">
          {renderImageDescriptionsSection()}
        </div>

        {/* Create content button */}
        <div className="mobile-social-hint-detail-image-section">
          <button
            className="mobile-social-hint-detail-image-create-button"
            onClick={handleCreateClick}
            disabled={totalUploadedFiles === 0}
          >
            <img src={iconAI} alt={t('socialHint.common.alt.ai')} className="mobile-social-hint-detail-image-create-button-icon" />
            {t('socialHint.common.actions.create')}
          </button>
        </div>

        {/* Bottom spacing */}
        <div className="mobile-social-hint-detail-image-bottom-spacing"></div>
      </div>

      {/* Create content modal */}
      {showCreateModal && (
        <div className="mobile-social-hint-detail-image-modal-overlay">
          <div className="mobile-social-hint-detail-image-modal">
            <div className="mobile-social-hint-detail-image-modal-header">
              <h3>{t('socialHint.detailImage.modal.create.title')}</h3>
              <button
                className="mobile-social-hint-detail-image-modal-close"
                onClick={handleCreateModalClose}
              >
                <img src={iconClose} alt={t('socialHint.common.alt.close')} />
              </button>
            </div>
            <div className="mobile-social-hint-detail-image-modal-content">
              <p>{t('socialHint.detailImage.modal.create.description')}</p>
              <div className="mobile-social-hint-detail-image-modal-files-count">
                <span>{t('socialHint.detailImage.modal.create.count', { count: totalUploadedFiles })}</span>
              </div>
              <p>{t('socialHint.detailImage.modal.create.question')}</p>
            </div>
            <div className="mobile-social-hint-detail-image-modal-actions">
              <button
                className="mobile-social-hint-detail-image-modal-button secondary"
                onClick={handleCreateModalClose}
              >
                {t('socialHint.detailImage.modal.create.cancel')}
              </button>
              <button
                className="mobile-social-hint-detail-image-modal-button primary"
                onClick={handleCreateConfirm}
              >
                {t('socialHint.detailImage.modal.create.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Queue confirmation modal */}
      {showQueueModal && (
        <div className="mobile-social-hint-detail-image-modal-overlay">
          <div className="mobile-social-hint-detail-image-modal">
            <div className="mobile-social-hint-detail-image-modal-header">
              <h3>{t('socialHint.detailImage.modal.queue.title')}</h3>
              <button
                className="mobile-social-hint-detail-image-modal-close"
                onClick={handleQueueModalClose}
              >
                <img src={iconClose} alt={t('socialHint.common.alt.close')} />
              </button>
            </div>
            <div className="mobile-social-hint-detail-image-modal-content">
              <div className="mobile-social-hint-detail-image-queue-success">
                <div className="mobile-social-hint-detail-image-queue-icon">✅</div>
                <p>{t('socialHint.detailImage.modal.queue.body')}</p>
                <div className="mobile-social-hint-detail-image-queue-info">
                  <span>{t('socialHint.detailImage.modal.queue.info')}</span>
                </div>
              </div>
            </div>
            <div className="mobile-social-hint-detail-image-modal-actions">
              <button
                className="mobile-social-hint-detail-image-modal-button primary"
                onClick={handleQueueModalClose}
              >
                {t('socialHint.detailImage.modal.queue.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSocialHintDetailImage;
